import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../BaseProps";
import * as Color from "../../general/Color";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";

import tokenStyles from "./styles.module.css";

/**
 * The TextColor component properties.
 */
export interface TextColorProps extends BaseProps {
  /**
   * The text color appearance. If not specified defaults to `dark grey 500`.
   */
  readonly appearance?: Color.RgbColor;
}

const useResolvedTextColorStyles = createComponentStyleHook<TextColorProps>({
  legacyStyles: () => import("./styles.module.scss"),
  tokenStyles,
  // No componentTokens — color is driven by the `appearance` prop via --text-color-appearance.
});

/**
 * Displays text content with specified color.
 */
export const TextColor: FC<PropsWithChildren<TextColorProps>> = (props) => {
  const { children, appearance = Color.DarkGrey[500], style } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedTextColorStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.TextColor, "textcolor"],
  });

  return (
    <StyleModeBoundary mode={mode}>
      <span
        {...baseHtmlProps}
        style={{
          ...style,
          ["--text-color-appearance" as string]: Color.toString(appearance),
        }}
      >
        {children}
      </span>
    </StyleModeBoundary>
  );
};
